/**
 * Chat Service
 *
 * Frontend service for interacting with the AI coach
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { getEmulatorHost, isUsingEmulators } from './emulator';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  model?: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface ChatResponse {
  conversationId: string;
  message: string;
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface StreamCallbacks {
  onChunk: (content: string) => void;
  onDone: (data: { model?: string; usage?: any }) => void;
  onError: (error: string) => void;
  onConversationId?: (conversationId: string) => void;
}

export interface ChatOptions {
  language?: string;
  mode?: 'weeklyCheckin' | 'careerInterview';
}

/**
 * Send a message to the AI coach (non-streaming, for backward compatibility)
 */
export async function sendChatMessage(
  message: string,
  conversationId?: string,
  options?: ChatOptions
): Promise<ChatResponse> {
  const functions = getFunctions();
  const chatFunction = httpsCallable<
    { message: string; conversationId?: string; language?: string },
    ChatResponse
  >(functions, 'chat');

  const result = await chatFunction({ message, conversationId, language: options?.language });
  return result.data;
}

/**
 * Send a message to the AI coach with streaming support
 */
export async function sendChatMessageStream(
  message: string,
  conversationId: string | undefined,
  callbacks: StreamCallbacks,
  options?: ChatOptions
): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  // In emulator mode, force a refresh so we don't reuse a cached/prod token.
  const idToken = await user.getIdToken(isUsingEmulators());

  // Determine the base URL for the functions
  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl = isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  // Create abort controller for timeout
  const controller = new AbortController();
  const apiTimeoutMs = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
  const streamTimeoutOverrideMs = process.env.REACT_APP_CHAT_STREAM_TIMEOUT_MS
    ? parseInt(process.env.REACT_APP_CHAT_STREAM_TIMEOUT_MS, 10)
    : NaN;

  // Streaming often takes longer than a typical REST call; keep a higher minimum.
  const timeoutMs = Number.isFinite(streamTimeoutOverrideMs)
    ? streamTimeoutOverrideMs
    : Math.max(Number.isFinite(apiTimeoutMs) ? apiTimeoutMs : 30000, 120000);

  const timeoutId = setTimeout(
    () => controller.abort(),
    Number.isFinite(timeoutMs) ? timeoutMs : 120000
  );

  try {
    const response = await fetch(`${baseUrl}/chatStream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        message,
        conversationId,
        language: options?.language,
        mode: options?.mode,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'conversationId' && callbacks.onConversationId) {
              callbacks.onConversationId(data.conversationId);
            } else if (data.type === 'chunk') {
              console.log('🔵 SSE chunk:', { contentLength: data.content?.length || 0, contentPreview: (data.content || '').substring(0, 50) });
              callbacks.onChunk(data.content);
            } else if (data.type === 'done') {
              callbacks.onDone({ model: data.model, usage: data.usage });
              return;
            } else if (data.type === 'error') {
              callbacks.onError(data.error);
              return;
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      callbacks.onError('Request timeout - chat service not responding');
    } else {
      throw error;
    }
  }
}
